import { useNavigate } from 'react-router-dom';
import { CircleUser, Star } from 'lucide-react';
import { navItemsFor } from 'config/nav';
import { Button } from 'components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from 'components/ui/dropdown-menu';
import { useFollowedTeams } from 'lib/followedTeams';
import { useAccountActions, useNavContext } from './useNavContext';

const MAX_NAV_TEAMS = 5;

// Desktop account entry point: a "Sign in" button when logged out, otherwise a
// dropdown with the account-placement links from config/nav.ts and Sign out.
export default function AccountMenu() {
    const navigate = useNavigate();
    const ctx = useNavContext();
    const { signIn, signOut } = useAccountActions();
    const { teams } = useFollowedTeams();

    // Render nothing until the session resolves so a logged-in user doesn't
    // see "Sign in" flash before the avatar.
    if (ctx.auth.loading) {
        return <div className="size-9" />;
    }

    if (!ctx.auth.loggedIn) {
        return <Button onClick={signIn}>Sign in</Button>;
    }

    const navTeams = teams.slice(0, MAX_NAV_TEAMS);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" aria-label="Account menu">
                    <CircleUser className="size-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-44">
                {navTeams.length > 0 && (
                    <>
                        {navTeams.map((followedTeam) => (
                            <DropdownMenuItem
                                key={followedTeam.id}
                                onSelect={() => navigate(`/team/${followedTeam.team.slug}`)}>
                                {followedTeam.isPrimary && (
                                    <Star className="size-4 fill-current text-primary" />
                                )}
                                {followedTeam.team.name}
                            </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                    </>
                )}
                {navItemsFor('account', ctx).map((item) => (
                    <DropdownMenuItem key={item.path} onSelect={() => navigate(item.path)}>
                        {item.title}
                    </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={signOut}>Sign out</DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
